/**
 * In-memory LRU cache for cross-space single-entity reads.
 *
 * Layered cache (memory → IDB → REST):
 *   - Memory: bounded LRU map (MAX_ENTRIES).
 *   - IDB:    services/idb/entityByIdIDB.ts persists across reloads.
 *   - REST:   GET /api/entities?ids={id} populates both layers on miss.
 *
 * Used by `useEntityById(id)` and any code path that needs the full skeleton
 * for a single entity ID without performing a dataspace-wide bulk load.
 */

import { useEffect } from 'react';
import { create } from 'zustand';
import { entityService } from '../services/entityService';
import { getEntityById, putEntityById } from '../services/idb/entityByIdIDB';
import { createFileLogger } from '../utils/logger';
import { registerByIdFallback, notifyChange as notifyRegistryChange, isFresherOrEqual } from './entityRegistry';
import type { BaseEntityType } from '../types/entities';

const log = createFileLogger('ENTITY_BY_ID_CACHE');

const MAX_ENTRIES = 1_000;

interface EntityByIdState {
  entries: Map<string, BaseEntityType>;
  inflight: Map<string, Promise<BaseEntityType | null>>;
  bumpVersion: number;
  get: (id: string) => BaseEntityType | undefined;
  fetch: (id: string) => Promise<BaseEntityType | null>;
  /** Coalesced batch fetch — deduplicates IDs, skips already-cached ones. */
  fetchBatch: (ids: string[]) => Promise<(BaseEntityType | null)[]>;
  put: (entity: BaseEntityType, opts?: { persist?: boolean }) => void;
  remove: (id: string) => void;
  clear: () => void;
}

function lruInsert(map: Map<string, BaseEntityType>, key: string, value: BaseEntityType): Map<string, BaseEntityType> {
  const next = new Map(map);
  next.delete(key);
  next.set(key, value);
  while (next.size > MAX_ENTRIES) {
    const oldestKey = next.keys().next().value as string | undefined;
    if (!oldestKey) break;
    next.delete(oldestKey);
  }
  return next;
}

export const useEntityByIdCache = create<EntityByIdState>((set, getStore) => ({
  entries: new Map(),
  inflight: new Map(),
  bumpVersion: 0,

  get: (id: string) => getStore().entries.get(id),

  put: (entity: BaseEntityType, opts: { persist?: boolean } = {}) => {
    const id = entity?._id;
    if (!id) return;
    let applied = false;
    set((s) => {
      if (!isFresherOrEqual(entity, s.entries.get(id))) return s;
      applied = true;
      return {
        entries: lruInsert(s.entries, id, entity),
        bumpVersion: s.bumpVersion + 1,
      };
    });
    if (applied && opts.persist !== false) {
      putEntityById(entity).catch(() => {});
    }
  },

  remove: (id: string) => {
    set((s) => {
      const next = new Map(s.entries);
      next.delete(id);
      return { entries: next, bumpVersion: s.bumpVersion + 1 };
    });
  },

  clear: () => {
    set({ entries: new Map(), inflight: new Map(), bumpVersion: 0 });
  },

  fetchBatch: async (ids: string[]): Promise<(BaseEntityType | null)[]> => {
    if (ids.length === 0) return [];
    const store = getStore();
    const results = new Map<string, BaseEntityType | null>();

    // 1. Memory cache. Skip falsy ids and already-cached ones. Reuse any
    //    in-flight per-id fetch so we never duplicate a request already running.
    const idbCandidates: string[] = [];
    const inflightWaits: Promise<void>[] = [];
    for (const id of new Set(ids)) {
      if (!id) continue;
      const cached = store.entries.get(id);
      if (cached) {
        results.set(id, cached);
        continue;
      }
      const pending = store.inflight.get(id);
      if (pending) {
        inflightWaits.push(pending.then((e) => { results.set(id, e); }));
      } else {
        idbCandidates.push(id);
      }
    }

    // 2. IDB-first reads for the remainder (no network). Misses fall through to REST.
    const restMissing: string[] = [];
    await Promise.all(
      idbCandidates.map(async (id) => {
        try {
          const fromIdb = await getEntityById(id);
          if (fromIdb) results.set(id, fromIdb);
          else restMissing.push(id);
        } catch {
          restMissing.push(id);
        }
      }),
    );

    // 3. Single batched REST query for the rest — queryEntities chunks `ids`
    //    internally, so this is one logical fetch rather than N parallel calls.
    let restFetched: BaseEntityType[] = [];
    if (restMissing.length > 0) {
      try {
        restFetched = (await entityService.queryEntities({ ids: restMissing.join(',') })) as BaseEntityType[];
      } catch (err) {
        log.warn(`[EntityByIdCache] fetchBatch REST failed for ${restMissing.length} ids`, err);
      }
      for (const e of restFetched) {
        if (e?._id) results.set(e._id, e);
      }
    }

    // 4. One cache write (single bumpVersion) for everything newly resolved,
    //    so subscribers re-render once rather than once per entity.
    const newlyResolved: BaseEntityType[] = [];
    for (const id of idbCandidates) {
      const r = results.get(id);
      if (r && r._id) newlyResolved.push(r);
    }
    if (newlyResolved.length > 0) {
      const stale = new Set<string>();
      set((s) => {
        let entries = s.entries;
        for (const e of newlyResolved) {
          if (!isFresherOrEqual(e, entries.get(e._id))) {
            stale.add(e._id);
            continue;
          }
          entries = lruInsert(entries, e._id, e);
        }
        return entries === s.entries ? s : { entries, bumpVersion: s.bumpVersion + 1 };
      });
      // A rejected write means something fresher already won the race — hand
      // callers that value instead of the stale body this fetch just resolved.
      if (stale.size > 0) {
        const latest = getStore().entries;
        for (const id of stale) {
          const fresher = latest.get(id);
          if (fresher) results.set(id, fresher);
        }
      }
      // Persist only REST-sourced bodies — IDB-sourced ones are already there.
      for (const e of restFetched) {
        if (e?._id && !stale.has(e._id)) putEntityById(e).catch(() => {});
      }
    }

    if (inflightWaits.length > 0) await Promise.all(inflightWaits);

    return ids.map((id) => results.get(id) ?? null);
  },

  fetch: async (id: string): Promise<BaseEntityType | null> => {
    if (!id) return null;
    const fromMemory = getStore().entries.get(id);
    if (fromMemory) return fromMemory;

    const existing = getStore().inflight.get(id);
    if (existing) return existing;

    const promise = (async () => {
      try {
        const fromIdb = await getEntityById(id);
        if (fromIdb) {
          let applied = false;
          set((s) => {
            if (!isFresherOrEqual(fromIdb, s.entries.get(id))) return s;
            applied = true;
            return {
              entries: lruInsert(s.entries, id, fromIdb),
              bumpVersion: s.bumpVersion + 1,
            };
          });
          // Something fresher already won the race — return that instead of
          // the stale IDB snapshot this call just resolved.
          return applied ? fromIdb : getStore().entries.get(id) ?? fromIdb;
        }

        const results = await entityService.queryEntities({ ids: id });
        const entity = (results as BaseEntityType[])[0] ?? null;
        if (entity) {
          let applied = false;
          set((s) => {
            if (!isFresherOrEqual(entity, s.entries.get(id))) return s;
            applied = true;
            return {
              entries: lruInsert(s.entries, id, entity),
              bumpVersion: s.bumpVersion + 1,
            };
          });
          if (applied) {
            putEntityById(entity).catch(() => {});
          } else {
            return getStore().entries.get(id) ?? entity;
          }
        }
        return entity;
      } catch (err) {
        log.warn(`[EntityByIdCache] fetch(${id}) failed`, err);
        return null;
      } finally {
        set((s) => {
          const inflight = new Map(s.inflight);
          inflight.delete(id);
          return { inflight };
        });
      }
    })();

    set((s) => {
      const inflight = new Map(s.inflight);
      inflight.set(id, promise);
      return { inflight };
    });

    return promise;
  },
}));

// Register as the registry's last-resort by-id lookup — see the comment on
// `registerByIdFallback` in entityRegistry.ts for why this fallback exists.
registerByIdFallback((id) => useEntityByIdCache.getState().entries.get(id));

// Bridge bumpVersion ticks → entityRegistry.subscribeToChanges, same pattern as
// spaceEntityStore/globalEntityStore/notesEntityStore, so listeners (e.g.
// spaceStore's window-title sync) re-check `entityRegistry.getById` whenever
// this cache is the one that actually received an update.
useEntityByIdCache.subscribe((state, prev) => {
  if (state.bumpVersion === prev.bumpVersion) return;
  notifyRegistryChange();
});

/**
 * React hook — returns the cached entity for `id`, kicking off a fetch if missing.
 * Returns `undefined` while the fetch is in flight.
 */
export function useEntityById(id: string | null | undefined): BaseEntityType | undefined {
  // Subscribe to entries so consumers re-render when the cache mutates.
  const entry = useEntityByIdCache((s) => (id ? s.entries.get(id) : undefined));

  // Side effects must run inside useEffect — calling fetch during render
  // violates the rules of hooks and triggers double-fetches under React
  // StrictMode. The inflight map dedups concurrent calls.
  const missing = !!id && !entry;
  useEffect(() => {
    if (!missing || !id) return;
    const cacheState = useEntityByIdCache.getState();
    if (cacheState.inflight.has(id)) return;
    log.log('useEntityById:fetchStart', { entityId: id });
    cacheState.fetch(id).then((fetched) => {
      log.log('useEntityById:fetchDone', { entityId: id, name: (fetched as any)?.name ?? null });
    }).catch(() => {});
  }, [id, missing]);

  return entry;
}
